import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, type ApiError } from '@whinerp/client';
import { useErrorMessage, useT } from '../../app/i18n.js';

/**
 * Asking for help from the portal (G-193): a case in the same queue, on the
 * same SLA clock, as one raised any other way. The number is shown so the
 * customer can quote it.
 */
export function PortalHelp({ token }: { token: string }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const [draft, setDraft] = useState({ subject: '', body: '' });
  const raise = useMutation({ mutationFn: () => api.raisePortalCase({ body: { token, ...draft } }) });
  if (raise.data?.number) {
    return <p data-portal-case={raise.data.number} className="rounded-card border border-rule p-4 text-body text-ink">{t('portal.help.raised', { values: { number: raise.data.number } })}</p>;
  }
  return (
    <section className="flex flex-col gap-2" data-portal-help>
      <h2 className="text-h3 text-ink">{t('portal.help.title')}</h2>
      <form className="flex flex-col gap-2" data-portal-help-form
        onSubmit={(event) => {
          event.preventDefault();
          raise.mutate();
        }}>
        <label className="flex flex-col gap-1 text-dense text-ink-600">
          {t('portal.help.subject')}
          <input className="h-input rounded-control border border-control bg-surface px-3 text-body text-ink" required maxLength={200} data-portal-help-subject
            value={draft.subject} onChange={(event) => setDraft({ ...draft, subject: event.target.value })} />
        </label>
        <label className="flex flex-col gap-1 text-dense text-ink-600">
          {t('portal.help.body')}
          <textarea className="min-h-24 rounded-control border border-control bg-surface p-3 text-body text-ink" required data-portal-help-body
            value={draft.body} onChange={(event) => setDraft({ ...draft, body: event.target.value })} />
        </label>
        {raise.data?.refusal ? (
          <p className="text-dense text-danger-text" data-portal-help-refused>
            {t(`portal.refusal.${raise.data.refusal.kind === 'expired' ? 'expired' : 'invalid'}.title`)}
          </p>
        ) : null}
        {raise.error ? <p className="text-dense text-danger-text">{errorText(raise.error as ApiError)}</p> : null}
        <div>
          <button type="submit" data-portal-help-send disabled={raise.isPending}
            className="h-touch rounded-control bg-bronze px-4 text-body text-surface disabled:opacity-60">
            {t('portal.help.send')}
          </button>
        </div>
      </form>
    </section>
  );
}
